import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook for deep linking to individual resources.
 * Reads ?resource=<id> from URL on mount and auto-opens matching resource.
 * Updates URL when resource is opened/closed.
 */
export const useResourceDeepLink = ({ resources, getDetailedContent, onSelect }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);

  // On mount, check for ?resource= param and auto-open
  useEffect(() => {
    const resourceId = searchParams.get('resource');
    if (resourceId && resources?.length) {
      const found = resources.find(r => r.id === resourceId);
      if (found) {
        if (getDetailedContent) {
          const detailed = getDetailedContent(found.id);
          if (detailed) {
            onSelect({ ...found, detailed });
          }
        } else {
          onSelect(found);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setResourceParam = useCallback((resourceId) => {
    setSearchParams({ resource: resourceId }, { replace: true });
  }, [setSearchParams]);

  const clearResourceParam = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const copyResourceLink = useCallback((resourceId) => {
    const url = new URL(window.location.href);
    url.searchParams.set('resource', resourceId);
    navigator.clipboard.writeText(url.toString()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return { setResourceParam, clearResourceParam, copyResourceLink, copied };
};
